import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { CountBadge } from '@/components/admin/CountBadge';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import { fetchDashboard } from '@/features/dashboard/api';
import {
  EVENT_STATUS_LABELS,
  bulkPublishEvents,
  copyEvent,
  fetchEvents,
  type EventBulkPublishItem,
  type EventItem,
  type EventStatus,
} from '@/features/events/api';
import { fetchEventTypes } from '@/features/masters/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

const PER_PAGE = 20;

const BASE_COLUMNS: Column<EventItem>[] = [
  { key: 'title', header: 'タイトル', primary: true, render: (e) => e.title },
  { key: 'type', header: '種別', render: (e) => e.type_name },
  {
    key: 'starts_at',
    header: '開催日時',
    // 面談は日時未定で作れる（D-173）
    render: (e) =>
      e.starts_at ? formatDateTime(e.starts_at) : '日程調整中',
  },
  { key: 'venue', header: '会場', render: (e) => e.venue ?? '—' },
];

// 理事・サクセスの一覧は自分が担当した回だけ（D-182）。面談は「誰との回か」が
// タイトルより重要なので、対象の受講生を1列で出す
const MEETING_MEMBER_COLUMN: Column<EventItem> = {
  key: 'meeting_member',
  header: '面談の相手（受講生）',
  className: 'whitespace-nowrap',
  render: (e) =>
    e.counterpart_role !== null ? (
      (e.member_name ?? '—')
    ) : (
      <span className="text-wptext-muted">—</span>
    ),
};

// 公開前後を出し分けるのは事務局の運用。他ロールには公開済みのものしか
// 届かないため、ステータス列は見せない（利用者フィードバック）
// 色は提出状況バッジ（EventSurveysTab）と同じ notice トークン（一目で下書き／公開を分ける）
const EVENT_STATUS_BADGE_CLASS: Record<EventStatus, string> = {
  draft: 'bg-wpnotice-warning/20 text-wptext-strong',
  published: 'bg-wpnotice-success/20 text-wptext-strong',
  closed: 'bg-wpborder/60 text-wptext-muted',
  archived: 'bg-wpborder/40 text-wptext-muted',
};

const STATUS_COLUMN: Column<EventItem> = {
  key: 'status',
  header: 'ステータス',
  className: 'whitespace-nowrap',
  render: (e) => (
    <span
      className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold ${EVENT_STATUS_BADGE_CLASS[e.status]}`}
    >
      {EVENT_STATUS_LABELS[e.status]}
    </span>
  ),
};

// 事務局の運営列（D-110 A-4）。一覧のまま「どのイベントの出欠が揃っていないか」を掴めるようにする
const OFFICE_OPS_COLUMNS: Column<EventItem>[] = [
  {
    key: 'attendance',
    header: '出欠 / 面談の相手',
    className: 'whitespace-nowrap',
    render: (e) => {
      // 面談（サクセス面談・1on1）は出欠を使わないので、代わりに誰と誰かを出す（D-177）
      if (e.counterpart_role !== null) {
        return (
          <>
            {e.member_name ?? '受講生未設定'}
            <span className="mx-1 text-wptext-muted">×</span>
            {e.counterpart_name ?? '担当未設定'}
          </>
        );
      }
      if (!e.attendance_required) return <span className="text-wptext-muted">出欠なし</span>;
      if (!e.target_count) return <span className="text-wptext-muted">対象未設定</span>;
      return (
        <span className="inline-flex items-center gap-1.5 tabular-nums">
          {e.answered_count} / {e.target_count}名
          <CountBadge count={e.unanswered_count ?? 0} label="未回答" showCount={false} />
        </span>
      );
    },
  },
  {
    key: 'capacity',
    header: '定員',
    className: 'whitespace-nowrap tabular-nums',
    render: (e) => {
      if (e.counterpart_role !== null) return <span className="text-wptext-muted">—</span>;
      if (e.type_code === 'monthly') return <span className="text-wptext-muted">—</span>;
      if (e.capacity === null) return <span className="text-wptext-muted">設定しない</span>;
      return (
        <>
          {e.capacity}名
          {(e.waitlist_count ?? 0) > 0 && (
            <span className="ml-1 text-wptext-muted">待機 {e.waitlist_count}</span>
          )}
        </>
      );
    },
  },
];

/** イベント一覧（指示書 §12 M4）。M2 の共通部品（ListTable）を使い回す。 */
export function EventListPage(): ReactElement {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOffice = user?.active_role === 'office';
  const isStaff = user?.active_role === 'director' || user?.active_role === 'success';
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<EventStatus | null>(null);
  const [copySource, setCopySource] = useState<EventItem | null>(null);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [bulkPublishIds, setBulkPublishIds] = useState<string[] | null>(null);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [bulkResults, setBulkResults] = useState<EventBulkPublishItem[] | null>(null);
  const [bulkSummary, setBulkSummary] = useState<{ published: number; failed: number } | null>(
    null,
  );
  const [clearSelectionToken, setClearSelectionToken] = useState(0);
  // 種別ごとの一覧（サイドバーの種別サブメニューから ?type= で開く / D-184）
  const [searchParams, setSearchParams] = useSearchParams();
  const typeId = searchParams.get('type');
  const queryClient = useQueryClient();

  async function onCopy(): Promise<void> {
    if (!copySource || copying) return;
    setCopying(true);
    setCopyError(null);
    try {
      const copied = await copyEvent(copySource.id);
      setCopySource(null);
      navigate(`/events/${copied.id}?copied=1`);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : 'コピーに失敗しました。');
      setCopySource(null);
    } finally {
      setCopying(false);
    }
  }

  async function onBulkPublish(): Promise<void> {
    if (!bulkPublishIds?.length || bulkPublishing) return;
    setBulkPublishing(true);
    setCopyError(null);
    try {
      const result = await bulkPublishEvents(bulkPublishIds);
      setBulkPublishIds(null);
      setBulkResults(result.results);
      setBulkSummary({ published: result.published_count, failed: result.failed_count });
      setClearSelectionToken((n) => n + 1);
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : '一括公開に失敗しました。');
      setBulkPublishIds(null);
    } finally {
      setBulkPublishing(false);
    }
  }

  const query = useQuery({
    queryKey: ['events', page, search, status, typeId],
    queryFn: () =>
      fetchEvents({
        page,
        per_page: PER_PAGE,
        q: search,
        status,
        type_id: typeId,
      }),
  });
  const isStudent = user?.active_role === 'student';
  const isSuccess = user?.active_role === 'success';
  // サクセスの主導線はサクセス面談。種別未指定で来たらそちらへ寄せる（D-297）
  const staffTypesForRedirect = useQuery({
    queryKey: ['event-types'],
    queryFn: fetchEventTypes,
    enabled: isSuccess && !typeId,
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (!isSuccess || typeId) return;
    const preferred = (staffTypesForRedirect.data ?? []).find(
      (t) => t.is_active && t.counterpart_role === 'success',
    );
    if (!preferred) return;
    setSearchParams({ type: preferred.id }, { replace: true });
  }, [isSuccess, typeId, staffTypesForRedirect.data, setSearchParams]);

  const dashboard = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard,
    enabled: isStudent || isOffice,
  });
  const todoByEvent = new Map<string, number>();
  for (const item of dashboard.data?.pending_event_actions ?? []) {
    todoByEvent.set(item.event_id, (todoByEvent.get(item.event_id) ?? 0) + 1);
  }
  const titleWithBadge: Column<EventItem> = {
    key: 'title',
    header: 'タイトル',
    primary: true,
    render: (e) => (
      <span className="inline-flex items-center gap-2">
        {e.title}
        <CountBadge
          count={todoByEvent.get(e.id) ?? 0}
          label={isOffice ? '欠席申請' : '未対応'}
          showCount={false}
        />
      </span>
    ),
  };
  const successScheduleTitle: Column<EventItem> = {
    key: 'title',
    header: 'タイトル',
    primary: true,
    render: (e) => (
      <span className="inline-flex items-center gap-2">
        {e.title}
        <CountBadge
          count={e.counterpart_role === 'success' && !e.starts_at ? 1 : 0}
          label="日程調整"
          showCount={false}
        />
      </span>
    ),
  };
  const studentColumns: Column<EventItem>[] = [
    titleWithBadge,
    ...BASE_COLUMNS.filter((column) => column.key !== 'title'),
  ];
  // 見出しに種別名を出す（一覧の1件目から取れる。0件でも動くよう種別マスタも見る）
  const eventTypes = useQuery({
    queryKey: ['event-types'],
    queryFn: fetchEventTypes,
    enabled: (isOffice || isStaff) && Boolean(typeId),
  });
  const selectedType = (eventTypes.data ?? []).find((t) => t.id === typeId);
  const typeName =
    selectedType?.name ?? (typeId ? (query.data?.items[0]?.type_name ?? null) : null);
  const isDirectorOneOnOne = selectedType?.counterpart_role === 'director';
  const isSuccessMeeting = selectedType?.counterpart_role === 'success';
  const isMeetingPairBulk = isDirectorOneOnOne || isSuccessMeeting;
  const isMonthlyTypeList = selectedType?.code === 'monthly';
  const officeColumns: Column<EventItem>[] = [
    titleWithBadge,
    ...BASE_COLUMNS.filter((column) => column.key !== 'title'),
    ...(isMonthlyTypeList ? [] : OFFICE_OPS_COLUMNS),
    STATUS_COLUMN,
  ];
  // 面談種別の一覧だけ「面談の相手」列を出す。月例会などに出すと意味が通らない（D-297）
  const isMeetingTypeList =
    selectedType?.counterpart_role != null ||
    (Boolean(typeId) && query.data?.items.some((e) => e.counterpart_role !== null) === true);
  const staffColumns: Column<EventItem>[] = isMeetingTypeList
    ? [
        ...(isSuccess ? [successScheduleTitle] : BASE_COLUMNS.filter((column) => column.key === 'title')),
        ...BASE_COLUMNS.filter((column) => column.key !== 'title' && column.key !== 'type'),
        MEETING_MEMBER_COLUMN,
      ]
    : BASE_COLUMNS;

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  const total = query.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title={typeName ? `${typeName}一覧` : 'イベント'}
        description={
          typeId
            ? isStaff
              ? 'この種別の担当イベントだけを表示しています。種別は左メニューから切り替えられます。'
              : 'この種別のイベントだけを表示しています。全種別を見るには左メニューの「イベント一覧」を開いてください。'
            : isOffice
              ? '月例会・分科会・EXゼミなどのイベントを管理します。'
              : isStaff
                ? '左メニューからイベント種別（月例会・分科会・1on1・サクセス面談など）を選んでください。処理する内容が種別ごとに異なります。'
                : '月例会・分科会・EXゼミなどの予定を確認できます。'
        }
        actions={
          isOffice ? (
            <>
              {!isMeetingPairBulk && (
                <button
                  type="button"
                  onClick={() => navigate('/events/recurring/new')}
                  className="btn-secondary"
                >
                  繰返し作成
                </button>
              )}
              {isDirectorOneOnOne && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/events/oneonone-import${typeId ? `?type=${encodeURIComponent(typeId)}` : ''}`,
                    )
                  }
                  className="btn-secondary"
                >
                  組み合わせCSVで一括作成
                </button>
              )}
              {isSuccessMeeting && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/events/success-meeting-import${typeId ? `?type=${encodeURIComponent(typeId)}` : ''}`,
                    )
                  }
                  className="btn-secondary"
                >
                  組み合わせCSVで一括作成
                </button>
              )}
            </>
          ) : undefined
        }
      />
      {copyError && (
        <Notice kind="error" onDismiss={() => setCopyError(null)}>
          {copyError}
        </Notice>
      )}
      <ListTable<EventItem>
        caption="イベント一覧"
        rows={query.data?.items ?? []}
        columns={
          isOffice
            ? officeColumns
            : isStaff
              ? staffColumns
              : studentColumns
        }
        rowKey={(e) => e.id}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        rowClassName={
          isOffice
            ? (e) => {
                if (e.status === 'draft') return 'bg-wpnotice-warning/10';
                if (e.status === 'published') return 'bg-wpnotice-success/5';
                return undefined;
              }
            : undefined
        }
        tabs={
          isOffice
            ? [
                { value: null, label: 'すべて', count: status === null ? total : undefined },
                { value: 'draft', label: '下書き' },
                { value: 'published', label: '公開済み' },
                { value: 'closed', label: '終了' },
                { value: 'archived', label: 'アーカイブ' },
              ]
            : [
                { value: null, label: 'すべて', count: status === null ? total : undefined },
                { value: 'published', label: '開催予定' },
                { value: 'closed', label: '終了' },
              ]
        }
        activeTab={status}
        onTabChange={(value) => {
          setStatus(value as EventStatus | null);
          setPage(1);
        }}
        searchValue={search}
        searchPlaceholder="タイトルで検索"
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        addLabel={
          isOffice && typeId ? (typeName ? `${typeName}の新規登録` : '新規登録') : undefined
        }
        // 種別ごとの一覧からだけ作る（汎用「イベント」一覧からは出さない / D-338）
        onAdd={
          isOffice && typeId ? () => navigate(`/events/new?type=${typeId}`) : undefined
        }
        rowActions={[
          { label: '詳細', onSelect: (e) => navigate(`/events/${e.id}`) },
          ...(isOffice
            ? [{ label: 'コピーして作る', onSelect: (e: EventItem) => setCopySource(e) }]
            : []),
        ]}
        page={page}
        perPage={PER_PAGE}
        total={total}
        onPageChange={setPage}
        emptyTitle="表示できるイベントがありません"
        bulkActions={
          isOffice
            ? [{ value: 'publish', label: '一括公開' }]
            : undefined
        }
        isRowSelectable={isOffice ? (e) => e.status === 'draft' : undefined}
        clearSelectionToken={clearSelectionToken}
        onBulkApply={(action, keys) => {
          if (action === 'publish' && keys.length > 0) {
            setBulkPublishIds(keys);
          }
        }}
      />
      <ConfirmDialog
        open={bulkPublishIds !== null}
        title="選択したイベントを一括公開しますか？"
        message={`${bulkPublishIds?.length ?? 0}件の下書きを公開します。対象者の確定・出欠行の作成・グループメッセージの生成が行われます。案内メールの予約は作成時に入っているものが、公開後に送信対象になります。対象者未設定など情報が足りないイベントは公開されず、結果画面にエラーが表示されます。`}
        confirmLabel={bulkPublishing ? '公開中…' : '一括公開する'}
        onConfirm={() => void onBulkPublish()}
        onCancel={() => !bulkPublishing && setBulkPublishIds(null)}
      />
      <Modal
        open={bulkResults !== null}
        title="一括公開の結果"
        wide
        onClose={() => {
          setBulkResults(null);
          setBulkSummary(null);
        }}
        footer={
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setBulkResults(null);
              setBulkSummary(null);
            }}
          >
            閉じる
          </button>
        }
      >
        {bulkSummary && (
          <p className="mb-3 text-wptext-strong">
            {bulkSummary.published}件を公開しました。
            {bulkSummary.failed > 0 && (
              <span className="text-wpnotice-error"> {bulkSummary.failed}件は公開できませんでした。</span>
            )}
          </p>
        )}
        <ul className="max-h-96 space-y-2 overflow-y-auto">
          {(bulkResults ?? []).map((item) => (
            <li
              key={item.event_id}
              className={`rounded-sm border px-3 py-2 ${item.ok ? 'border-wpnotice-success/40 bg-wpnotice-success/5' : 'border-wpnotice-error/40 bg-wpnotice-error/5'}`}
            >
              <div className="font-semibold text-wptext-strong">{item.title}</div>
              {item.ok ? (
                <div className="text-wpnotice-success">公開しました</div>
              ) : (
                <div className="text-wpnotice-error">{item.error ?? '公開できませんでした。'}</div>
              )}
            </li>
          ))}
        </ul>
      </Modal>
      <ConfirmDialog
        open={copySource !== null}
        title="このイベントをコピーして下書きを作りますか？"
        message="案内文・会場・対象者の条件・担当者・課題/アンケートの割り当てを、新しい下書きとして複製します。出欠・回答・席次・当日写真・メッセージはコピーしません。開催日時もそのまま入るので、公開前に直してください。"
        confirmLabel="コピーして作る"
        onConfirm={() => void onCopy()}
        onCancel={() => setCopySource(null)}
      />
    </>
  );
}
